import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';

import {
  isNotEmpty,
  validateEmail,
  getLeadIdFromLocalStorage,
  saveLeadIdToLocalStorage,
  getbrowserUUIDFromLocalStorage,
  contactListingAddress,
  getListingUnitIdentifier,
} from 'src/utils/utils';
import { loadPhoneUtils } from 'src/utils/loadPhoneUtils';

import { BuildingPageContext } from '../../pages/[urlParam]/[urlBuildParam]';
import { UnitPageContext } from '../../pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { NeighborhoodPageContext } from '../../pages/[urlParam]';
import { getContactFormContextData } from '../../src/utils/buildingUtils';

import { UnitFromQuickViewType } from 'types';
import PopUpThanks from '../PopUpThanks';
import Loading from 'components/Loading';
import Expert from '../Unit/Expert';
import { useAuth } from '../Auth/AuthProvider';
import { useMutation } from '@apollo/client';
import { SEND_CONTACT_US_FORM } from 'apolloClient/mutations/alerts';
import { pushContactUsEvent, ContactFormLocation } from 'src/gtm';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';
import Agents from 'components/Agents';
import { Agent, MappedAgent } from 'apolloClient/types/Agents';
import { Listing } from 'apolloClient/types/Units';
import PhoneInput from 'components/PhoneInput';
import SmsConsentCheckbox from 'components/SmsConsent/SmsConsentCheckbox';
import SmsConsentPopup from 'components/SmsConsent/SmsConsentPopup';
import { useSmsConsent } from 'components/SmsConsent/useSmsConsent';
import useSnackbar from 'components/SnackBar/useSnackbar';

type Props = {
  isHeaderForm: boolean;
  isRequestInfoBuilding?: boolean;
  isRequestInfoBuildingOwner?: boolean;
  isRequestInfoUnitOwner?: boolean;
  listingFromQuickView?: Listing;
  unitFromQuickView?: UnitFromQuickViewType;
  experts?: Agent[];
  customText?: string;
  formLocation?: ContactFormLocation;
};

const ContactForm: React.FC<Props> = (props: Props) => {
  const {
    isHeaderForm,
    isRequestInfoBuilding,
    isRequestInfoBuildingOwner,
    isRequestInfoUnitOwner,
    listingFromQuickView,
    unitFromQuickView,
    experts,
    customText,
    formLocation = 'page_popup',
  } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [defaultAgent, setDefaultAgent] = useState<MappedAgent | null>(null);
  const router = useRouter();

  const {
    building: buildingData,
    url: buildingPageUrl,
    medianPrice,
  } = useContext(BuildingPageContext) || {};

  const {
    listing,
    building: listingBuilding,
    neighborhood: listingNeighborhood,
    url: unitPageUrl,
  } = useContext(UnitPageContext) || {};
  const { neighborhood } = useContext(NeighborhoodPageContext) || {};
  const { me } = useAuth();
  const [fieldsValid, setFieldsValid] = useState({
    Name: true,
    Email: true,
    Phone: true,
    Comments: true,
  });

  const [contactFormData, setContactFormData] = useState({
    Name: '',
    Phone: '',
    Email: '',
    Comments: '',
    FormLoaded6tY4bPYk: '',
    PhoneNumber6tY4bPYk: '',

    submitState: {
      isSubmitted: false,
      isTouched: false,
      isHeaderFormActive: false,
      submitting: false,
      recaptchaFailed: false,
    },
  });
  const [dataType, setDataType] = useState(false);
  const [sendContactUsForm] = useMutation(SEND_CONTACT_US_FORM);

  const sms = useSmsConsent();
  const { showError } = useSnackbar();
  // Set once submit succeeds but the SMS popup still needs to be resolved before
  // revealing the normal success UI. Also acts as a double-submit guard.
  const [pendingSuccess, setPendingSuccess] = useState<null | {
    email: string;
    phone: string;
    leadId: string | null;
  }>(null);
  // In-flight guard set synchronously before the first await, so a rapid second
  // click can't fire a duplicate sendContactUsForm during reCAPTCHA/the mutation.
  const isSubmittingRef = useRef(false);

  const defaultCommentsValue = useMemo(
    () =>
      customText
        ? customText
        : listingFromQuickView
        ? `I'm interested in getting more information about UNIT #${
            listingFromQuickView?.unit?.data?.attributes?.unique
          } at ${
            unitFromQuickView?.building.data.attributes.name
          }, ${contactListingAddress(listingFromQuickView?.address, '')}`
        : // ? `I'm interested in getting more information about UNIT #${unitFromQuickView?.unique} at ${unitFromQuickView?.building.data.attributes.name}, ${unitFromQuickView?.building.data.attributes.primaryAddress}`
        buildingData?.name && isRequestInfoBuilding
        ? `Please send me the floor plan for ${buildingData?.name}`
        : buildingData?.name && isRequestInfoBuildingOwner
        ? `I own in ${buildingData?.name}. Please get me on regular updates on this building's sales and market reports.`
        : getListingUnitIdentifier(listing) && isRequestInfoUnitOwner
        ? `I would like to discuss selling my condo #${getListingUnitIdentifier(
            listing
          )} at ${listingBuilding?.name}, ${listing?.address?.streetNumber} ${
            listing?.address?.streetName
          }, ${listingNeighborhood?.city.data.attributes.name}, ${
            listing?.address?.zipCode
          }`
        : getListingUnitIdentifier(listing)
        ? `I'm interested in getting more information about UNIT #${getListingUnitIdentifier(
            listing
          )} at ${listingBuilding?.name}, ${contactListingAddress(
            listing?.address,
            listingNeighborhood?.city?.data?.attributes?.name || ''
          )}`
        : buildingData?.name
        ? `I'm interested in getting more information about ${buildingData.name}`
        : neighborhood?.name
        ? `I'm interested in getting more information about ${neighborhood?.name}`
        : '',
    [
      listingFromQuickView,
      buildingData,
      isRequestInfoBuilding,
      isRequestInfoBuildingOwner,
      isRequestInfoUnitOwner,
      listing,
      listingBuilding,
      listingNeighborhood,
      neighborhood,
      customText,
    ]
  );

  // Set the load timestamp only once on mount — never resets the honeypot clock
  useEffect(() => {
    setContactFormData((prevData) => ({
      ...prevData,
      FormLoaded6tY4bPYk: Date.now().toString(),
    }));
  }, []);

  // Update user/context-dependent fields without touching the timestamp
  useEffect(() => {
    setContactFormData((prevData) => ({
      ...prevData,
      Name: me?.fullName ?? '',
      Phone: me?.phoneNumber || '',
      Email: me?.email ?? '',
      Comments: defaultCommentsValue,
      agent:
        localStorage.getItem('agent') || router.query.agent?.toString() || null,
      leadId: getLeadIdFromLocalStorage(),
    }));

    // Shown as stored until the phone utils load and turn it into E.164.
    const storedPhone = me?.phoneNumber;
    if (!storedPhone) return;
    let current = true;
    loadPhoneUtils().then(({ toPhoneValue }) => {
      if (!current) return;
      setContactFormData((prevData) => ({
        ...prevData,
        Phone: toPhoneValue(storedPhone),
      }));
    });
    return () => {
      current = false;
    };
  }, [me, defaultCommentsValue]);

  // Unique per instance: a page can mount this form twice (inline + dialog).
  const phoneErrorId = useId();
  const showPhoneError =
    contactFormData.submitState.isTouched && !fieldsValid.Phone;

  const validateFields = (isValidPhone: (phone: string) => boolean) => {
    let nameValid = isNotEmpty(contactFormData.Name);
    let commentsValid = isNotEmpty(contactFormData.Comments);
    let emailValid = validateEmail(contactFormData.Email);
    let phoneValid = isValidPhone(contactFormData.Phone);

    setContactFormData((prevState) => ({
      ...prevState,
      submitState: { ...prevState.submitState, isTouched: true },
    }));

    setFieldsValid({
      Name: nameValid,
      Comments: commentsValid,
      Email: emailValid,
      Phone: phoneValid,
    });

    return nameValid && commentsValid && emailValid && phoneValid;
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setContactFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));

    if (contactFormData.submitState.isTouched) {
      if (e.target.name === 'Name' || e.target.name === 'Comments') {
        let valid = isNotEmpty(e.target.value);
        setFieldsValid((prev) => ({ ...prev, [e.target.name]: valid }));
      } else if (e.target.name === 'Email') {
        let validEmail = validateEmail(e.target.value);
        setFieldsValid((prev) => ({ ...prev, Email: validEmail }));
      } else if (e.target.name === 'Phone') {
        const phone = e.target.value;
        loadPhoneUtils().then(({ isValidPhone }) =>
          setFieldsValid((prev) => ({ ...prev, Phone: isValidPhone(phone) }))
        );
      }
    }
  };

  const checkForRent = () => {
    const fullUrl = router.asPath.toLowerCase();
    const containsForRent =
      fullUrl.includes('forrent') || fullUrl.includes('for-rent');

    if (containsForRent) {
      return 'RENT';
    }

    return '';
  };
  const onContactFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Guard: a submit is already in flight, or the inquiry is sent and the SMS
    // popup is open. Prevents duplicate lead writes from a double-click.
    if (isSubmittingRef.current || pendingSuccess) return;
    isSubmittingRef.current = true;
    setDataType(true);
    setContactFormData((formData) => ({
      ...formData,
      submitState: { ...formData.submitState, recaptchaFailed: false },
    }));
    const token = (await onVerify()) || '';
    let isValidPhone: (phone: string) => boolean;
    try {
      ({ isValidPhone } = await loadPhoneUtils());
    } catch (error) {
      // Release the guard so a later submit can retry the import.
      console.error(error);
      isSubmittingRef.current = false;
      showError({
        message: 'Your request could not be sent. Please try again.',
      });
      return;
    }
    // if (!token) {
    //   setContactFormData((formData) => ({
    //     ...formData,
    //     submitState: { ...formData.submitState, recaptchaFailed: true },
    //   }));
    //   return;
    // }
    if (validateFields(isValidPhone)) {
      let payload = {
        postData: {
          ...contactFormData,
          ...getContactFormContextData(
            buildingData || listingBuilding,
            // Page context has no listing on e.g. the search page, so a
            // quick-view inquiry needs its own listing, not just context's.
            listingFromQuickView || listing,
            buildingPageUrl,
            unitPageUrl,
            router.asPath
          ),
        },
        defaultMessage: defaultCommentsValue,
        isHeaderForm: isHeaderForm,
      };

      const leadIdFromLocalStorage = getLeadIdFromLocalStorage();

      if (payload.postData.LeadType == '')
        payload.postData.LeadType = checkForRent();

      setContactFormData((formData) => ({
        ...formData,
        submitState: { ...formData.submitState, submitting: true },
      }));

      const browserUUID = getbrowserUUIDFromLocalStorage() || undefined;

      sendContactUsForm({
        variables: {
          ...payload.postData,
          leadId: leadIdFromLocalStorage,
          browserUUID,
          medianPrice: medianPrice || null,
          RecaptchaToken: token,
          isOwnerRequest:
            isRequestInfoUnitOwner || isRequestInfoBuildingOwner || false,
          smsConsent: sms.submitConsentValue,
        },
      })
        .then((data) => {
          try {
            const oldLeadId = getLeadIdFromLocalStorage() || null;
            const newLeadId = data?.data?.sendContactUsForm?.leadId;

            if ((!oldLeadId && newLeadId) || oldLeadId !== newLeadId) {
              saveLeadIdToLocalStorage(newLeadId);
            }
          } catch (error) {
            console.error(error);
          }
          try {
            pushContactUsEvent(
              contactFormData.Email.trim(),
              contactFormData.Phone.trim(),
              formLocation
            );
          } catch (error) {
            console.error(error);
          }
          if (sms.shouldPromptAfterSubmit) {
            // Inquiry captured; defer success until the SMS popup is resolved.
            const submittedLeadId =
              data?.data?.sendContactUsForm?.leadId ??
              getLeadIdFromLocalStorage() ??
              null;
            setPendingSuccess({
              email: contactFormData.Email.trim(),
              phone: contactFormData.Phone.trim(),
              leadId: submittedLeadId,
            });
            setContactFormData((formData) => ({
              ...formData,
              submitState: { ...formData.submitState, submitting: false },
            }));
            sms.openPopup();
          } else {
            // No popup means consent was sent as YES (box checked or already
            // consented) — reflect it locally so we don't prompt again here.
            sms.markConsented();
            setContactFormData((formData) => ({
              ...formData,
              submitState: {
                ...formData.submitState,
                isSubmitted: true,
                submitting: false,
              },
            }));
          }
        })
        .catch((error) => {
          console.error(error);
          // Clear the loading state so the button doesn't stay stuck on "Sending".
          setContactFormData((formData) => ({
            ...formData,
            submitState: { ...formData.submitState, submitting: false },
          }));
        })
        .finally(() => {
          isSubmittingRef.current = false;
        });
    } else {
      // Validation failed — allow the user to correct and resubmit.
      isSubmittingRef.current = false;
    }
  };

  const onVerify = useCallback(async () => {
    if (!executeRecaptcha) return;

    const token = await executeRecaptcha('contact_form');
    return token;
  }, [executeRecaptcha]);

  const onClosingPopup = () => {
    setContactFormData((prevState) => ({
      ...prevState,
      submitState: { ...prevState.submitState, isSubmitted: false },
    }));
  };

  // Reveal the normal success UI after the SMS popup has been resolved.
  const revealSuccess = () => {
    setPendingSuccess(null);
    setContactFormData((formData) => ({
      ...formData,
      submitState: { ...formData.submitState, isSubmitted: true },
    }));
  };

  return isHeaderForm && contactFormData.submitState.isSubmitted ? (
    <div
      data-type={dataType && 'contact_us_header'}
      id="contact-us-header-ga4"
      className="flex flex-col border-t w-72 md:w-[420px] contact-us-success tab-pane active in border-beige-dark"
    >
      <div className="flex justify-center px-1 my-4 text-20-28-0.3 text-center font-sohneKraftig text-gold-darker">
        <span className="md:w-4/5">
          Your request has been sent,{' '}
          {defaultAgent ? defaultAgent.firstName : 'agent'} will contact you
          within 24 hours
        </span>
      </div>
      <div className="flex">
        <Agents
          experts={experts}
          showSecondAgent={false}
          onDefaultAgent={setDefaultAgent}
          specificPage={
            checkForRent() === 'RENT' ? 'RENTAL_UNIT_PAGE' : 'NOT_SPECIFIC'
          }
        />
      </div>
    </div>
  ) : (
    <>
      <form
        data-type={dataType && 'contact_us_body'}
        id="contact-us-body-ga4"
        className={classnames('relative flex flex-col sm:pb-5', {
          'justify-center w-auto mt-8 text-16-26-0.3 align-center md:w-full':
            isHeaderForm,
          'text-16-26-0.3 md:w-[150%]': !isHeaderForm,
        })}
        onSubmit={onContactFormSubmit}
      >
        {/* Honeypot: phone-like name lures bots; display:none (not off-screen) keeps browser autofill out. */}
        <input
          type="text"
          className="cf-aux-field"
          aria-label="PhoneNumber6tY4bPYk"
          value={contactFormData.PhoneNumber6tY4bPYk}
          name="PhoneNumber6tY4bPYk"
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
        <input
          name="Name"
          className={classnames({
            'border border-gray form-input h-[46px] pl-[16px] text-16-21-0.3 md:text-14-21-0.3 ':
              isHeaderForm,
            'px-4 py-2 border border-gray h-[46px] text-16-21-0.3 md:text-14-21-0.3 ':
              !isHeaderForm,
          })}
          type="text"
          placeholder="Your Name"
          value={contactFormData.Name}
          onChange={handleChange}
        />
        {contactFormData.submitState.isTouched && !fieldsValid.Name && (
          <span className="field-validation-error">
            <span className="pl-3 text-red">Name is required</span>
          </span>
        )}
        <PhoneInput
          name="Phone"
          aria-label="Phone"
          aria-invalid={showPhoneError}
          aria-describedby={showPhoneError ? phoneErrorId : undefined}
          className={classnames({
            'border border-gray -my-1 form-input h-[46px] pl-[16px] text-16-21-0.3 md:text-14-21-0.3 ':
              isHeaderForm,
            'px-4 py-2 -my-1 border border-gray h-[46px] text-16-21-0.3 md:text-14-21-0.3 ':
              !isHeaderForm,
          })}
          placeholder="Phone"
          value={contactFormData.Phone}
          onChange={handleChange}
        />
        {showPhoneError && (
          <span className="field-validation-error" id={phoneErrorId}>
            <span className="pl-3 text-red">Phone number is not valid</span>
          </span>
        )}
        <input
          name="Email"
          className={classnames({
            'border border-gray  form-input h-[46px] pl-[16px] text-16-21-0.3 md:text-14-21-0.3 ':
              isHeaderForm,
            'px-4 py-2  border border-gray h-[46px] text-16-21-0.3 md:text-14-21-0.3 ':
              !isHeaderForm,
          })}
          type="email"
          placeholder="Email"
          value={contactFormData.Email}
          onChange={handleChange}
        />
        {contactFormData.submitState.isTouched && !fieldsValid.Email && (
          <div className="field-validation-error">
            <div className="pl-3 text-red">Email address is invalid</div>
          </div>
        )}
        <textarea
          className={classnames({
            'border form-input h-[100px] mt-[27px] pl-[16px] pt-[15px] text-16-21-0.3 md:text-14-21-0.3 ':
              isHeaderForm,
            'px-[16px] pt-[17px] pb-2 border border-gray h-[100px] mt-[27px] text-16-21-0.3 md:text-14-21-0.3 ':
              !isHeaderForm,
          })}
          name="Comments"
          id=""
          cols={10}
          rows={3}
          placeholder="Your message"
          value={contactFormData.Comments}
          onChange={handleChange}
        />
        {contactFormData.submitState.isTouched && !fieldsValid.Comments && (
          <div className="field-validation-error">
            <div className="pl-3 text-red">Comments is required</div>
          </div>
        )}
        <p className="pt-2 md:pb-2 md:pr-4 cs-legend-block text-13-20-0.3 text-justify">
          By submitting this form, you agree to be contacted by Blackbook
          Properties via call and email. To opt-out, you can reply “stop” at any
          time or click the unsubscribe link in the emails. Message and data
          rates may apply.{' '}
          <a
            target="_blank"
            href="/terms-of-use/"
            className="text-underline text-gold-darker"
          >
            Terms of Use
          </a>{' '}
          and{' '}
          <a
            target="_blank"
            href="/privacy-policy/"
            className="text-underline text-gold-darker"
          >
            Privacy Policy
          </a>
        </p>
        {sms.isCheckboxVisible && (
          <SmsConsentCheckbox
            id={`SmsConsent_${isHeaderForm ? 'ContactHeader' : 'RequestInfo'}`}
            checked={sms.checked}
            onChange={sms.setChecked}
            className="mt-[10px] text-13-20-0.3"
          />
        )}
        {/* {contactFormData.submitState.recaptchaFailed && (
          <div className="field-validation-error">
            <div className="pl-3 text-red">
              Verification failed. Please try again or disable your ad blocker.
            </div>
          </div>
        )} */}
        {isHeaderForm && (
          <button
            id="Contact_Us_Send_Header"
            className={classnames(
              'hover-animation self-end w-32 h-[44px] mt-6 text-12-18-0.3 uppercase',
              {
                ' bg-gold-hover hover:bg-gold-hover md:text-16-26-0.3 border-gold-hover hover:border-gold-hover w-[143px]':
                  contactFormData.submitState.submitting,
                'bg-gold hover:bg-gold-hover md:text-16-26-0.3  w-[143px]':
                  !contactFormData.submitState.submitting,
              }
            )}
          >
            {contactFormData.submitState.submitting ? 'Sending' : 'Send'}
          </button>
        )}
        {!isHeaderForm && (
          <PopUpThanks
            buttonIsLoading={contactFormData.submitState.submitting}
            popupOpen={
              !contactFormData.submitState.isHeaderFormActive &&
              contactFormData.submitState.isSubmitted
            }
            closePopup={onClosingPopup}
          />
        )}
        {contactFormData.submitState.submitting && (
          <Loading overlay label="Sending your request…" />
        )}
      </form>
      <SmsConsentPopup
        open={sms.popupOpen}
        onYes={() => {
          // Fire-and-forget: the inquiry is already captured, never block the
          // success UI on the consent upgrade.
          if (pendingSuccess) void sms.onPopupYes(pendingSuccess);
          revealSuccess();
        }}
        onDismiss={() => {
          sms.onPopupDismiss();
          revealSuccess();
        }}
      />
    </>
  );
};

export default ContactForm;
